<script lang="ts">
  import { storyblokEditable } from "@storyblok/svelte";
  import GridContainer from "$lib/molecules/GridContainer.svelte";
  import GridItem from "$lib/atoms/GridItem.svelte";
  import type { HeroStoryblok } from "$types/components";
  import Image from "$lib/atoms/Image.svelte";
  import Icon from "$lib/atoms/Icon.svelte";
  import { IconType } from "$types/atoms";
  import VideoVimeo from "$lib/molecules/VideoVimeo.svelte";
  import VideoV2 from "$lib/molecules/VideoV2.svelte";
  import ButtonLink from "$lib/atoms/ButtonLink.svelte";
  import { isThemeLight, type ThemeColor } from "$lib/utils/helpers";
  import { displayType } from "$lib/utils/breakpoints";
  import TextCard from "$lib/elements/TextCard.svelte";
  import CountdownClock from "$lib/atoms/CountdownClock.svelte";
  import StoryblokComponent from "$lib/StoryblokComponent.svelte";

  export let blok: HeroStoryblok;
  export let theme: ThemeColor = "grey";

  let wrapper: HTMLElement;
  let inner: HTMLElement;
  let fullscreenVideoSrc = "";

  $: button = blok.textCard?.[0]?.buttons?.[0] ?? null;
  $: isLayoutCompact = (blok.layout ?? "normal") === "compact";

  $: hasAnchor = Boolean(blok.anchors?.length);
  $: hasLink = Boolean(blok.link?.length);
  $: hasMedia = Boolean(blok.media?.length);
  $: hasOnlyTitle = !(hasAnchor || hasLink || hasMedia);
  $: {
    if (
      blok.fullscreenImage &&
      (blok?.media?.length ?? 0) > 0 &&
      blok?.media?.[0]?.media
    ) {
      fullscreenVideoSrc =
        $displayType !== "mobile" && blok?.media[0]?.mediaDesktop?.filename
          ? blok.media[0].mediaDesktop?.filename
          : blok.media[0].media?.filename;
    }
  }
</script>

<div
  bind:this={wrapper}
  class={`section max-w-full hero ${theme} ${blok?.classname ?? ""}`}
  class:has-anchor={hasAnchor}
  class:has-fullscreen-image={blok.fullscreenImage &&
    blok.media?.[0].component === "image"}
  class:has-fullscreen-video={blok.fullscreenImage &&
    blok.media?.[0].component === "video"}
  class:has-only-title={hasOnlyTitle}
  class:compact={isLayoutCompact}
  use:storyblokEditable={blok}
>
  <div
    bind:this={inner}
    class="movable h-full {blok.textCard?.length ? 'h-full w-full' : ''}"
  >
    <div
      class="section-inner {blok.textCard?.length
        ? 'flex flex-col justify-end'
        : ''}"
      class:!py-[48px]={isLayoutCompact}
      class:!space-y-[48px]={isLayoutCompact}
    >
      <GridContainer
        class="section-max-width"
        alignItems={`${hasAnchor ? "end" : "start"}`}
      >
        <!-- Text Card -->
        {#if blok.textCard?.length && blok.textCard[0].heading?.length}
          <div
            class="relative z-10 px-4 {blok.textCard[0].textAlignmentDesktop ===
            'text-center'
              ? 'md:mx-auto'
              : 'md:ml-0'} {blok.textCard[0].textAlignment === 'text-center'
              ? 'mx-auto'
              : ''}"
          >
            <TextCard hero blok={blok.textCard[0]} />
          </div>
        {/if}

        {#if hasAnchor}
          <GridItem col={12} colMd={6}>
            <div class="content">
              <h1 class="headline type:h2">{blok.headline}</h1>
            </div>
          </GridItem>
          <GridItem col={12} offset={0} colMd={3} offsetMd={3}>
            <ul class="anchor-list" style="position:relative">
              {#if blok.anchors}
                {#each blok.anchors as anchor}
                  <li class="anchor">
                    <a
                      class="type:body-small anchor-link"
                      href={`#${anchor.text?.toLowerCase().replace(" ", "-")}`}
                      >{anchor.text}
                      <Icon type={IconType.ARROW_DOWN} />
                    </a>
                  </li>
                {/each}
              {/if}
            </ul>
          </GridItem>
        {:else}
          <GridItem
            col={12}
            colMd={blok.headlineCompact ? 10 : 12}
            offsetMd={blok.headlineCompact ? 1 : 0}
          >
            <div
              class="content"
              class:px-4={!(blok.headlineLeft ?? false)}
            >
              <h1
                class="headline type:h1"
                class:is-compact={blok.headlineCompact ?? false}
                class:is-left={blok.headlineLeft ?? false}
                class:!mb-0={isLayoutCompact}
              >
                {blok.headline}
              </h1>
              {#if blok.link && blok.link[0]}
                <ButtonLink blok={blok.link[0]} isLight={isThemeLight(theme)} />
              {/if}
            </div>
          </GridItem>
        {/if}
        {#if blok.countdownClock?.length}
          <GridItem col={12} class="relative z-10">
            <CountdownClock blok={blok.countdownClock[0]} />
          </GridItem>
        {/if}
      </GridContainer>
      {#if !blok.fullscreenImage && hasMedia}
        <GridContainer class="section-max-width">
          <GridItem col={12}>
            <div class="media" class:!mt-0={isLayoutCompact}>
              {#if blok.media?.[0]?.component === "image" && blok.media[0].object}
                <Image
                  media={blok.media[0].object}
                  aspect={"16-9"}
                  lgSize="83"
                  smSize="83"
                  width="1200"
                  height="675"
                  loading="eager"
                />
              {/if}
              {#if blok.media && blok.media[0] && blok.media[0].component === "videoVimeo"}
                <VideoVimeo blok={blok.media[0]} />
              {/if}
              {#if blok.media?.[0]?.component === "video" && blok.media[0].media?.filename?.length}
                <VideoV2 blok={blok.media[0]} maxHeight="600px" />
              {/if}
            </div>
          </GridItem>
        </GridContainer>
      {/if}
      {#if !blok.fullscreenImage && isLayoutCompact && blok.textCard?.[0] && button}
        <GridContainer class="section-max-width">
          <GridItem col={12}>
            <div class="flex gap-[24px] md:flex-row md:justify-between">
              <!-- The heroHorziontal prop is understoood by TextCard -->
              <StoryblokComponent blok={blok.textCard[0]} heroHorizontal />
            </div>
          </GridItem>
        </GridContainer>
      {/if}

      {#if blok.fullscreenImage}
        {#if blok.media?.[0]?.component === "image" && blok.media[0].object}
          <div class="fullscreen-image h-full">
            <Image
              media={blok.media[0].object}
              class="test h-full"
              lgSize="100"
              smSize="100"
            />
          </div>
        {/if}
        {#if blok.media?.[0]?.component === "video" && blok.media[0].media?.filename?.length}
          <div class="fullscreen-video">
            <video
              style="width:100%;height:100%;object-fit:cover"
              autoplay
              muted
              playsinline
              loop
              src={fullscreenVideoSrc}
            />
            {#if blok.media[0].useTranslucentOverlay}
              <div class="absolute inset-0 bg-[rgba(0,0,0,0.30)]" />
            {/if}
          </div>
        {/if}
      {/if}
    </div>
  </div>
</div>

<style lang="postcss">
  .hero {
    padding-top: 0;
    position: relative;

    &.green,
    &.blue,
    &.yellow,
    &.has-fullscreen-image {
      padding-left: 20px;
      padding-right: 20px;
    }
    &.green .section-inner {
      background: rgb(var(--color-green-primary));
      color: #ffffff;

      .headline {
        color: #ffffff;
      }
    }

    &.blue .section-inner {
      background: rgb(var(--color-blue-primary));
      color: #ffffff;

      .headline {
        color: #ffffff;
      }
    }

    &.yellow .section-inner {
      background: rgb(var(--color-yellow-primary));
    }

    &.has-fullscreen-image {
      aspect-ratio: 375/760;

      @screen sm {
        aspect-ratio: 1440/1111;
      }

      .section-inner {
        height: 100%;
      }
    }

    &.has-fullscreen-video .section-inner {
      overflow: hidden;
      background-color: transparent !important;
    }

    &.hero-technology .section-inner {
      padding-top: 120px;
      padding-bottom: 57vh;
    }
  }

  .fullscreen-image {
    position: relative;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 2;
    aspect-ratio: 1/1;

    @screen sm {
      position: absolute;
      aspect-ratio: 375/760;
    }

    :global(.test picture),
    :global(.test img) {
      object-fit: cover;
      height: 100%;
      width: 100%;
    }
  }

  .fullscreen-video {
    position: absolute;
    inset: 0;
  }

  .section-inner {
    padding-top: 60px;
    padding-bottom: 40px;
    @screen md {
      padding-top: 120px;
      padding-bottom: 100px;
    }
    background: rgb(var(--color-warm-grey));
    border-radius: var(--border-radius-section);
    position: relative;
    z-index: 5;
  }

  .has-only-title {
    .section-inner {
      padding-top: 120px;
      padding-bottom: 120px;
      @screen md {
        padding-top: 206px;
        padding-bottom: 206px;
      }
    }
  }

  .hero.small-hero {
    padding-left: 20px;
    padding-right: 20px;
    .section-inner {
      padding-top: 100px;
      padding-bottom: 100px;
    }
  }
  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 5;
  }
  .anchor-list {
    width: 100%;
  }

  .anchor {
    width: 100%;

    &:not(:first-child) {
      border-top: 1px solid var(--color-stroke-dark-rgba);
    }

    &:first-child .anchor-link {
      padding-top: 0;
    }

    &:last-child .anchor-link {
      padding-bottom: 0;
    }
  }

  .anchor-link {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
    text-decoration: none;
    color: rgb(var(--color-text-dark));
  }

  .headline {
    margin: 0 auto 40px;
    &:not(.is-left) {
      text-align: center;
    }
    &.is-left {
      text-wrap: balance;
    }
  }

  .has-anchor {
    .headline {
      text-align: left;
      margin-bottom: 40px;

      @screen sm {
        margin: 0;
      }
    }
  }

  .media {
    width: 100%;
    margin-top: 56px;
    @screen lg {
      margin-top: 80px;
    }
  }
</style>
